import { brukerApiName } from '@klp/user/config';
import { generateApiRoute } from '@klp/core/api/helpers';

import { baseApi } from '@klp/core/api';
import {
    calculateFordelingApiName,
    kostkategoriApiName,
    processApiName,
    prosjektApiName,
    StatisticsModuleName,
    vurderingsskjemaApiName,
} from '../config';
import type { Bruker } from '@klp/user';
import { useDispatch } from 'react-redux';
import type {
    AssignProsjektProcessRequest,
    ConfluenceContent,
    CreateProsjektProcessRequest,
    IKostkategori,
    IProsjektStatistics,
    IProsjektStatisticsRequest,
    IProsjektStatisticsResponse,
    ISoProsjektDto,
    IVurderingsskjema,
    KostnadskjemaResponse,
    KostnadskjemaSummary,
    KostnadskjemaUserInput,
    Kostnadstype,
    KostnadstypeRequest,
    Tilleggssatser,
    ProsjektFullmakt,
} from './types';
import type {
    GodkjenningProsessResponse,
    GodkjenningProsessSimple,
} from '../interfaces';

export enum ConsultantRoles {
    DRIFTS_FORVALTER = 'isDriftsForvalter',
    FORVALTER = 'isForvalter',
    HANDEL_FORVALTER = 'isHandelForvalter',
    KONTOR_FORVALTER = 'isKontorForvalter',
    OKONOMI = 'isOkonomi',
    QA_OKONOMI = 'isQAOkonomi',
}

export enum Activities {
    VURDER_RELEVANS = 'Activity_1vr6y7a',
    TILDEL_OKONOMI = 'Activity_1rdcqtp',
    UTFOR_REGISTRERING_D365 = 'Activity_0hw9rg3',
    KONTROLLER_D365_REGISTRERING = 'Activity_1bk3epl',
    GODKJENNING_AV_KONTRAKT = 'Activity_1b84rij',
    TILKNYTT_DOKUMENT_TIL_KONTRAKT = 'Activity_15vq4i6',
    GJENNOMGANGSMOTE = 'Activity_1m3ak5n',
}

export enum ConsultantReviewerRoles {
    FORVALTER = 'Forvalter',
    OKONOMI = 'Okonomi',
    QA_OKONOMI = 'Qa',
}

type GetProsjektProcessRequest = {
    portfolio?: string;
    region?: string;
    fromDate?: string;
    statuses?: Array<string>;
    byggnummer?: string;
    // Partial match on prosjektnummer, byggnummer or prosjektnavn
    sok?: string;
    // E-mail of a user. Gives every process with an active task assigned to
    // the user, and the other filters aren't used.
    tildeltTil?: string;
};

const simpleProcessEndpoint = 'simple';
const prosjektFilter = 'filter';
const assignProcess = 'tildelProsess';
const projectTypes = 'prosjekttypes';
const projectSkjemaTypes = 'prosjektskjematypes';
const projectStep = 'prosjektsteg';
const projectStatus = 'prosjektregistreringstatus';
const sendReminder = 'sendreminder';
const role = 'rolle';
const processinstans = 'processinstans';
const prosessDefinisjon = 'prosessdefinisjon';
const xml = 'xml';
const confluence = 'confluence';
const content = 'content';
const page = 'page';
const config = 'config';
const kostnadskjema = 'kostnadskjema';
const kostnadstype = 'kostnadstype';
const restore = 'restore';
const averagecost = 'averagecost';
const calculatekostnad = 'calculatekostnad';
const satser = 'satser';
const okonomi = 'okonomi';
const fullmakt = 'fullmakt';

export const prosjektApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getProsjektProcessSimple: build.query<
            Array<GodkjenningProsessSimple>,
            GetProsjektProcessRequest
        >({
            query: ({ ...params }) => ({
                url: generateApiRoute([processApiName, simpleProcessEndpoint]),
                params: { ...params },
            }),
            providesTags: ['ProsjektProsess'],
        }),
        getProsjektProcessById: build.query<
            GodkjenningProsessResponse,
            { prosjektId: string; culture?: string }
        >({
            query: ({ prosjektId, culture }) => ({
                url: generateApiRoute([processApiName, prosjektId]),
                params: { culture },
            }),
            providesTags: ['ProsjektProsess'],
        }),
        getProsjektProcessByProcessId: build.query<
            GodkjenningProsessResponse,
            { prosessInstansId: string; culture?: string }
        >({
            query: ({ prosessInstansId, culture }) => ({
                url: generateApiRoute([
                    processApiName,
                    processinstans,
                    prosessInstansId,
                ]),
                params: { culture },
            }),
            providesTags: ['ProsjektProsess'],
        }),
        getProsjektConfluenceContent: build.query<
            ConfluenceContent,
            { id: string }
        >({
            query: ({ id }) => ({
                url: generateApiRoute([config, confluence, content, page, id]),
            }),
        }),
        getProcessDefinitionXml: build.query<
            string,
            { processDefinitionId: string }
        >({
            query: ({ processDefinitionId }) => ({
                url: generateApiRoute([
                    processApiName,
                    prosessDefinisjon,
                    processDefinitionId,
                    xml,
                ]),
                responseHandler: 'text',
            }),
        }),
        getProcessDefinitionsList: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, prosessDefinisjon]),
            }),
        }),
        getCandidateGroupRoles: build.query<Array<string>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, role]),
            }),
        }),
        getUsers: build.query<Array<Bruker>, void>({
            query: () => ({
                url: generateApiRoute([brukerApiName]),
            }),
        }),

        getUsersByProsjektRoleAndRegion: build.query<
            Array<Bruker>,
            { rolle: string; region: string | undefined }
        >({
            query: ({ rolle, region }) => ({
                url: generateApiRoute([brukerApiName]),
                params: { rolle, region },
            }),
        }),

        getVurderingsskjema: build.query<
            IVurderingsskjema,
            { prosessInstansId?: string }
        >({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([
                    processApiName,
                    vurderingsskjemaApiName,
                    prosessInstansId,
                ]),
                method: 'GET',
            }),
            providesTags: ['Vurderingsskjema'],
        }),
        postVurderingsskjemaCalculcation: build.mutation<
            IVurderingsskjema,
            IVurderingsskjema
        >({
            query: (body) => ({
                url: generateApiRoute([
                    processApiName,
                    vurderingsskjemaApiName,
                    calculateFordelingApiName,
                ]),
                method: 'POST',
                body,
            }),
        }),
        postVurderingsskjema: build.mutation<
            IVurderingsskjema,
            IVurderingsskjema
        >({
            query: (body) => ({
                url: generateApiRoute([
                    processApiName,
                    vurderingsskjemaApiName,
                ]),
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Vurderingsskjema'],
        }),
        postVurderingsskjemaAutosave: build.mutation<
            IVurderingsskjema,
            IVurderingsskjema
        >({
            query: (body) => ({
                url: generateApiRoute([
                    processApiName,
                    vurderingsskjemaApiName,
                ]),
                method: 'POST',
                body,
            }),
            // No invalidatesTags - autosave should not trigger refetch
        }),
        getKostkategori: build.query<Array<IKostkategori>, void>({
            query: () =>
                generateApiRoute([processApiName, kostkategoriApiName]),
        }),
        getProsjektStatus: build.query<Array<string>, void>({
            query: () => generateApiRoute([processApiName, projectStatus]),
        }),
        getProjectTypes: build.query<
            Map<string, number>,
            { includeInitiatives: boolean }
        >({
            query: ({ includeInitiatives }) => ({
                url: generateApiRoute([processApiName, projectTypes]),
                params: { includeInitiatives },
            }),
        }),
        getProsjektSkjemaTypes: build.query<Record<string, number>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, projectSkjemaTypes]),
            }),
        }),
        getProjectStepTypes: build.query<
            Record<string, number>,
            { type: number; onlyStartingSteps?: boolean }
        >({
            query: ({ type, onlyStartingSteps = false }) => ({
                url: generateApiRoute([processApiName, projectStep, type]),
                params: { onlyStartingSteps },
            }),
            providesTags: ['ProjectStepTypes'],
        }),
        getFilteredProsjekt: build.query<
            Array<ISoProsjektDto>,
            {
                type?: 'Prosjekt';
                name?: string;
                filterExisting?: boolean;
            }
        >({
            query: ({ type, name, filterExisting }) => ({
                url: generateApiRoute([prosjektApiName, prosjektFilter]),
                params: { type, name, filterExisting },
            }),
            providesTags: ['SuperOfficeProsjekt'],
        }),
        createProsjektProcess: build.mutation<
            GodkjenningProsessResponse,
            CreateProsjektProcessRequest
        >({
            query: ({
                type,
                steg,
                superOfficeProsjektId,
                prosjektskjemaType,
                rekkefolgenummer,
            }) => ({
                url: generateApiRoute([processApiName]),
                method: 'POST',
                headers: { 'Content-Type': 'text/plain' },
                params: {
                    type,
                    steg,
                    superOfficeProsjektId,
                    prosjektskjemaType,
                    rekkefolgenummer,
                },
            }),
            invalidatesTags: [
                'ProsjektProsess',
                'SuperOfficeProsjekt',
                'ProsjektStatistics',
            ],
        }),
        deleteProsjektProcess: build.mutation<
            void,
            { prosessInstansId: string }
        >({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([processApiName, prosessInstansId]),
                method: 'DELETE',
            }),
            invalidatesTags: [
                'ProsjektProsess',
                'SuperOfficeProsjekt',
                'ProsjektStatistics',
            ],
        }),
        assignProsjektProcess: build.mutation<
            void,
            AssignProsjektProcessRequest
        >({
            query(request) {
                return {
                    url: generateApiRoute([processApiName, assignProcess]),
                    method: 'POST',
                    body: request,
                };
            },
            invalidatesTags: ['ProsjektProsess', 'ProsjektStatistics'],
        }),
        sendReminder: build.mutation<void, { prosessInstansId: string }>({
            query({ prosessInstansId }) {
                return {
                    url: generateApiRoute([processApiName, sendReminder]),
                    method: 'POST',
                    params: { prosessInstansId },
                };
            },
        }),
        getProsjektStatistics: build.query<
            IProsjektStatistics,
            IProsjektStatisticsRequest
        >({
            query: ({ ansvarlig, byggNummer }) => ({
                url: generateApiRoute([processApiName, StatisticsModuleName]),
                params: { ansvarlig, byggNummer },
            }),
            transformResponse: (response: IProsjektStatisticsResponse) => {
                return {
                    TildeltMeg: response.TildeltMeg,
                    TilGodkjenning: response.TilGodkjenning,
                    Godkjent: response.Godkjent,
                    Avvist: response.Avvist,
                };
            },
            providesTags: ['ProsjektStatistics'],
        }),
        getKostnadskjema: build.query<
            KostnadskjemaSummary,
            { prosessInstansId: string }
        >({
            query: ({ prosessInstansId }) => ({
                url: generateApiRoute([
                    processApiName,
                    kostnadskjema,
                    prosessInstansId,
                ]),
            }),
        }),
        getKostnadskjemaAverage: build.query<
            number,
            { prosessInstansId: string; fromDate?: string }
        >({
            query: ({ prosessInstansId, fromDate }) => ({
                url: generateApiRoute([
                    processApiName,
                    kostnadskjema,
                    prosessInstansId,
                    averagecost,
                ]),
                params: { fromDate },
            }),
        }),
        calculateKostnadskalkyle: build.query<
            KostnadskjemaResponse,
            KostnadskjemaUserInput
        >({
            query: (body) => {
                return {
                    url: generateApiRoute([
                        processApiName,
                        kostnadskjema,
                        calculatekostnad,
                    ]),
                    method: 'POST',
                    body,
                };
            },
        }),
        createKostnadskjema: build.mutation<
            KostnadskjemaResponse,
            KostnadskjemaUserInput & {
                shouldInvalidateVurderingsskjema?: boolean;
            }
        >({
            query: ({ shouldInvalidateVurderingsskjema: _, ...rest }) => {
                return {
                    url: generateApiRoute([processApiName, kostnadskjema]),
                    method: 'POST',
                    body: rest,
                };
            },
            invalidatesTags: (
                _result,
                _error,
                { shouldInvalidateVurderingsskjema },
            ) => (shouldInvalidateVurderingsskjema ? ['Vurderingsskjema'] : []),
        }),
        getTilleggssatser: build.query<Tilleggssatser, void>({
            query: () => ({
                url: generateApiRoute([processApiName, kostnadskjema, satser]),
            }),
        }),
        getKostnadstype: build.query<Array<Kostnadstype>, void>({
            query: () => ({
                url: generateApiRoute([processApiName, kostnadstype]),
            }),
        }),
        getKostnadstypeById: build.query<Kostnadstype, { id: number }>({
            query: ({ id }) => ({
                url: generateApiRoute([processApiName, kostnadstype, id]),
            }),
        }),
        upsertKostnadstype: build.mutation<Kostnadstype, KostnadstypeRequest>({
            query: (body) => ({
                url: generateApiRoute([processApiName, kostnadstype]),
                method: 'POST',
                body,
            }),
        }),
        deleteKostnadstype: build.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: generateApiRoute([processApiName, kostnadstype, id]),
                method: 'DELETE',
            }),
        }),
        restoreKostnadstype: build.mutation<void, { id: number }>({
            query: ({ id }) => ({
                url: generateApiRoute([
                    processApiName,
                    kostnadstype,
                    restore,
                    id,
                ]),
                method: 'POST',
            }),
        }),
        getProsjektFullmakter: build.query<Array<ProsjektFullmakt>, void>({
            query: () => ({
                url: generateApiRoute([prosjektApiName, okonomi, fullmakt]),
            }),
        }),
    }),
});

type ProsjektApiTagType = Parameters<
    typeof prosjektApi.util.invalidateTags
>[0][number];

export const useProsjektInvalidateTags = (tags: Array<ProsjektApiTagType>) => {
    const dispatch = useDispatch();
    return () => dispatch(prosjektApi.util.invalidateTags(tags));
};

export const {
    useGetProsjektProcessSimpleQuery,
    useGetProsjektProcessByIdQuery,
    useGetProsjektProcessByProcessIdQuery,
    useGetUsersQuery,
    useLazyGetUsersByProsjektRoleAndRegionQuery,
    useGetUsersByProsjektRoleAndRegionQuery,
    useGetVurderingsskjemaQuery,
    useLazyGetVurderingsskjemaQuery,
    usePostVurderingsskjemaMutation,
    usePostVurderingsskjemaAutosaveMutation,
    useGetKostkategoriQuery,
    useLazyGetKostkategoriQuery,
    usePostVurderingsskjemaCalculcationMutation,
    useGetProsjektStatusQuery,
    useLazyGetFilteredProsjektQuery,
    useCreateProsjektProcessMutation,
    useDeleteProsjektProcessMutation,
    useAssignProsjektProcessMutation,
    useGetProjectTypesQuery,
    useGetProsjektSkjemaTypesQuery,
    useGetProjectStepTypesQuery,
    useLazyGetProjectStepTypesQuery,
    useSendReminderMutation,
    useGetCandidateGroupRolesQuery,
    useLazyGetCandidateGroupRolesQuery,
    useGetProsjektStatisticsQuery,
    useGetProcessDefinitionXmlQuery,
    useGetProcessDefinitionsListQuery,
    useLazyGetProcessDefinitionXmlQuery,
    useLazyGetProcessDefinitionsListQuery,
    useGetProsjektConfluenceContentQuery,
    useLazyGetProsjektConfluenceContentQuery,
    useGetKostnadskjemaAverageQuery,
    useLazyGetKostnadskjemaAverageQuery,
    useGetKostnadskjemaQuery,
    useCalculateKostnadskalkyleQuery,
    useCreateKostnadskjemaMutation,
    useGetTilleggssatserQuery,
    useGetKostnadstypeQuery,
    useLazyGetKostnadstypeQuery,
    useGetKostnadstypeByIdQuery,
    useLazyGetKostnadstypeByIdQuery,
    useUpsertKostnadstypeMutation,
    useDeleteKostnadstypeMutation,
    useRestoreKostnadstypeMutation,
    useGetProsjektFullmakterQuery,
} = prosjektApi;
